import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Bike,
  Check,
  Pause,
  Star,
  X,
  TrendingUp,
  Phone,
  Mail,
  MapPin,
  Calendar,
  Shield,
  Building2,
  Truck,
  Package,
  RotateCcw,
} from 'lucide-react';
import { GoogleMap, Marker } from '@react-google-maps/api';
import { useMaps } from '@contexts/MapsContext';
import { ridersService } from '@services/riders.service';
import { cn } from '@lib/utils';
import { PageGuard } from '@components/PageGuard';
import { PermissionGate } from '@components/PermissionGate';
import { ForceEndTripModal } from '@components/delivery/ForceEndTripModal';
import type { DeliveryAssignment } from '@apptypes/index';

const statusChip: Record<string, string> = {
  active:               'bg-[var(--state-success-bg)] text-[var(--state-success)]',
  pending_verification: 'bg-[var(--state-warning-bg)] text-[var(--state-warning)]',
  suspended:            'bg-[var(--state-danger-bg)] text-[var(--state-danger)]',
  deactivated:          'bg-[var(--bg-base)] text-[var(--text-muted)]',
};

// §11c: a tier is ordered, so it rides the intensity ramp. `premium` was AMBER,
// i.e. --state-warning, which made every premium rider look like a problem in
// the fleet list. A tier is not a state.
//
// chekit (top) stays solid forest, premium steps down to progress-3, merchant is
// inert. The rank now reads as a magnitude, which is what it is.
const tierChip: Record<string, string> = {
  chekit:   'bg-[var(--brand-forest)] text-white',
  premium:  'bg-[var(--progress-3)] text-[var(--progress-3-fg)]',
  merchant: 'bg-[var(--bg-base)] text-[var(--text-muted)]',
};

// §11c: the green intensity ramp, following the delivery leg.
//
// `accepted` and `picked_up` were AMBER, i.e. --state-warning. That is worse
// than a flat ramp: amber already means NEEDS ATTENTION, so a rider who had
// simply picked the order up rendered as a problem in the queue. A semantic
// colour firing on a non-event is a broken alarm.
const assignmentStatusChip: Record<string, string> = {
  broadcasted:'bg-[var(--progress-1)] text-[var(--progress-1-fg)]',
  assigned:   'bg-[var(--progress-1)] text-[var(--progress-1-fg)]',
  accepted:   'bg-[var(--progress-2)] text-[var(--progress-2-fg)]',
  picked_up:  'bg-[var(--progress-3)] text-[var(--progress-3-fg)]',
  in_transit: 'bg-[var(--progress-4)] text-[var(--progress-4-fg)]',
  delivered:  'bg-[var(--state-success-bg)] text-[var(--state-success)]',
  failed:     'bg-[var(--state-danger-bg)] text-[var(--state-danger)]',
  reassigned: 'bg-[var(--bg-base)] text-[var(--text-muted)]',
};

const MAPS_API_KEY = (import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string) || '';

const MAP_CONTAINER_STYLE = { width: '100%', height: '320px' };
const MAP_OPTIONS = {
  disableDefaultUI: false,
  streetViewControl: false,
  mapTypeControl: false,
  fullscreenControl: true,
} as const;

function SuspendModal({
  onConfirm,
  onClose,
  isPending,
}: {
  onConfirm: (reason: string) => void;
  onClose: () => void;
  isPending: boolean;
}) {
  const [reason, setReason] = useState('');
  return (
    <div className="fixed inset-0 z-60 flex items-center justify-center p-4 bg-[rgba(15,61,30,0.45)]">
      <div className="bg-white rounded-3xl shadow-lift w-full max-w-lg p-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-[18px] font-semibold text-[var(--text-primary)]">Suspend Rider</h2>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full flex items-center justify-center text-[var(--text-muted)] hover:bg-[var(--bg-base)] hover:text-[var(--text-primary)]"
          >
            <X className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>

        <label className="block text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)] mb-1.5">
          Reason *
        </label>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          placeholder="Why is this rider being suspended?"
          className="w-full rounded-2xl border border-[var(--border-default)] bg-white px-4 py-3 text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--border-strong)] resize-none"
        />

        <div className="flex justify-end gap-3 mt-8">
          <button
            onClick={onClose}
            className="rounded-full border border-[var(--border-default)] bg-white px-5 py-2.5 text-[14px] font-medium text-[var(--text-primary)] hover:border-[var(--border-strong)]"
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm(reason.trim())}
            disabled={isPending || !reason.trim()}
            className="rounded-full border border-[var(--state-danger-border)] bg-[var(--state-danger-bg)] px-5 py-2.5 text-[14px] font-medium text-[var(--state-danger)] hover:border-[var(--state-danger)] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isPending ? 'Suspending...' : 'Suspend'}
          </button>
        </div>
      </div>
    </div>
  );
}

export const RiderDetailPage = () => {
  const { riderId } = useParams<{ riderId: string }>();
  const queryClient = useQueryClient();
  const [showSuspend, setShowSuspend] = useState(false);
  const [showForceEnd, setShowForceEnd] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [deliveriesPage, setDeliveriesPage] = useState(1);
  const [deliveryStatusFilter, setDeliveryStatusFilter] = useState('');

  const { data: rider, isLoading } = useQuery({
    queryKey: ['rider', riderId],
    queryFn: () => ridersService.get(riderId!),
    enabled: !!riderId,
    refetchInterval: 6000,
  });

  const { data: availStats } = useQuery({
    queryKey: ['rider-avail-stats', riderId],
    queryFn: () => ridersService.availabilityStats(riderId!),
    enabled: !!riderId,
    refetchInterval: 30_000,
  });

  const { data: deliveriesData, isLoading: deliveriesLoading } = useQuery({
    queryKey: ['rider-deliveries', riderId, deliveriesPage, deliveryStatusFilter],
    queryFn: () =>
      ridersService.listDeliveries(riderId!, {
        page: deliveriesPage,
        limit: 10,
        status: deliveryStatusFilter || undefined,
      }),
    enabled: !!riderId,
  });

  const { mapsLoaded, mapsLoadError } = useMaps();

  const [mapReady, setMapReady] = useState(false);
  const mapRef = useRef<google.maps.Map | null>(null);
  const riderMarkerRef = useRef<google.maps.Marker | null>(null);

  const onMapLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
    setMapReady(true);
  }, []);

  // Create / update rider marker imperatively: avoids declarative Marker timing race
  useEffect(() => {
    if (!mapReady || !mapRef.current) return;

    if (rider?.currentLatitude != null && rider?.currentLongitude != null) {
      const pos = { lat: rider.currentLatitude, lng: rider.currentLongitude };
      const label = {
        text: rider.availability === 'on_delivery' ? 'D' : 'R',
        // A literal, not var(--…), on purpose: Google Maps rasterises marker
        // labels itself and does not resolve CSS custom properties.
        color: '#ffffff',
        fontSize: '11px',
        fontWeight: '700',
      };
      if (riderMarkerRef.current) {
        riderMarkerRef.current.setPosition(pos);
        riderMarkerRef.current.setLabel(label);
      } else {
        riderMarkerRef.current = new google.maps.Marker({
          map: mapRef.current,
          position: pos,
          label,
        });
      }
      mapRef.current.panTo(pos);
    } else {
      riderMarkerRef.current?.setMap(null);
      riderMarkerRef.current = null;
    }
  }, [mapReady, rider?.currentLatitude, rider?.currentLongitude, rider?.availability]);

  // Remove marker on unmount
  useEffect(() => {
    return () => {
      riderMarkerRef.current?.setMap(null);
      riderMarkerRef.current = null;
    };
  }, []);

  const onSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['rider', riderId] });
    queryClient.invalidateQueries({ queryKey: ['riders'] });
    setActionError(null);
  };
  const onError = (e: unknown) => setActionError((e as Error)?.message ?? 'Action failed.');

  const verifyMutation = useMutation({
    mutationFn: () => ridersService.verify(riderId!),
    onSuccess,
    onError,
  });

  const suspendMutation = useMutation({
    mutationFn: (reason: string) => ridersService.suspend(riderId!, reason),
    onSuccess: () => {
      setShowSuspend(false);
      onSuccess();
    },
    onError,
  });

  const upgradeMutation = useMutation({
    mutationFn: () => ridersService.upgrade(riderId!),
    onSuccess,
    onError,
  });

  const reinstateMutation = useMutation({
    mutationFn: () => ridersService.reinstate(riderId!),
    onSuccess,
    onError,
  });

  const downgradeMutation = useMutation({
    mutationFn: () => ridersService.downgrade(riderId!),
    onSuccess,
    onError,
  });

  const deactivateMutation = useMutation({
    mutationFn: () => ridersService.deactivate(riderId!),
    onSuccess,
    onError,
  });

  const NAIROBI = { lat: -1.2921, lng: 36.8219 };

  const mapCenter = useMemo(() => {
    if (rider?.currentLatitude != null && rider?.currentLongitude != null) {
      return { lat: rider.currentLatitude, lng: rider.currentLongitude };
    }
    const ad = rider?.activeDelivery;
    if (ad?.dropoffLatitude != null && ad?.dropoffLongitude != null) {
      return { lat: ad.dropoffLatitude, lng: ad.dropoffLongitude };
    }
    return NAIROBI;
  }, [rider]);

  const hasLiveLocation = rider?.currentLatitude != null && rider?.currentLongitude != null;

  const locationAge = useMemo(() => {
    if (!rider?.lastLocationAt) return null;
    const seconds = Math.floor((Date.now() - new Date(rider.lastLocationAt).getTime()) / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    return `${Math.floor(seconds / 3600)}h ago`;
  }, [rider?.lastLocationAt]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[var(--bg-base)] p-8 text-center text-[14px] text-[var(--text-muted)]">
        Loading rider...
      </div>
    );
  }

  if (!rider) {
    return (
      <div className="min-h-screen bg-[var(--bg-base)] p-8 text-center text-[14px] text-[var(--state-danger)]">
        Rider not found.
      </div>
    );
  }

  const fullName = [rider.userFirstName, rider.userLastName].filter(Boolean).join(' ') || '—';
  const canVerify    = rider.status === 'pending_verification';
  const canSuspend   = rider.status === 'active' || rider.status === 'pending_verification';
  const canUpgrade   = rider.riderTier === 'merchant' && rider.status === 'active';
  const canDowngrade = (rider.riderTier === 'chekit' || rider.riderTier === 'premium') && rider.status === 'active';
  const canReinstate = rider.status === 'suspended';
  const canDeactivate = rider.status !== 'deactivated';

  return (
    <PageGuard permission="riders.read">
      <div className="min-h-screen bg-[var(--bg-base)] p-8 space-y-8">
        {/* Back */}
        <Link
          to="/riders"
          className="inline-flex items-center gap-2 text-[13px] text-[var(--text-muted)] hover:text-[var(--text-primary)]"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
          Back to Riders
        </Link>

        {/* Page header */}
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-2">
            <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)]">
              Rider profile
            </p>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-[var(--brand-forest)] text-white flex items-center justify-center">
                <Bike className="w-6 h-6" strokeWidth={1.5} />
              </div>
              <div>
                <h1 className="font-display text-[28px] font-medium tracking-[-0.02em] text-[var(--text-primary)]">
                  {fullName}
                </h1>
                <div className="flex items-center gap-2 mt-1">
                  <span
                    className={cn(
                      'rounded-full px-2.5 py-1 text-[11px] font-semibold',
                      statusChip[rider.status] ?? 'bg-[var(--bg-base)] text-[var(--text-muted)]',
                    )}
                  >
                    {rider.status.replace(/_/g, ' ')}
                  </span>
                  <span
                    className={cn(
                      'rounded-full px-2.5 py-1 text-[11px] font-semibold capitalize',
                      tierChip[rider.riderTier] ?? 'bg-[var(--bg-base)] text-[var(--text-muted)]',
                    )}
                  >
                    {rider.riderTier}
                  </span>
                  <span className="rounded-full px-2.5 py-1 text-[11px] font-semibold bg-[var(--bg-base)] text-[var(--text-muted)] capitalize">
                    {rider.availability.replace(/_/g, ' ')}
                  </span>
                  {rider.isOnProbation && (
                    <span className="rounded-full px-2.5 py-1 text-[11px] font-semibold bg-[var(--state-warning-bg)] text-[var(--state-warning)]">
                      On probation
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 flex-wrap justify-end">
            {canVerify && (
              <PermissionGate permission="kyc.verify">
                <button
                  onClick={() => verifyMutation.mutate()}
                  disabled={verifyMutation.isPending}
                  className="inline-flex items-center gap-1.5 rounded-full bg-[var(--brand-forest)] px-4 py-2 text-[13px] font-semibold text-white transition-all duration-200 disabled:opacity-50 hover:brightness-110 hover:shadow-[0_12px_32px_rgba(124,179,66,0.45)] active:brightness-95"
                >
                  <Check className="w-3.5 h-3.5" strokeWidth={1.5} />
                  Verify
                </button>
              </PermissionGate>
            )}
            {canUpgrade && (
              <PermissionGate permission="riders.upgrade">
                <button
                  onClick={() => upgradeMutation.mutate()}
                  disabled={upgradeMutation.isPending}
                  className="inline-flex items-center gap-1.5 rounded-full border border-[var(--border-default)] bg-white px-4 py-2 text-[13px] font-medium text-[var(--text-primary)] hover:border-[var(--border-strong)] transition-all duration-200 disabled:opacity-50"
                >
                  <TrendingUp className="w-3.5 h-3.5" strokeWidth={1.5} />
                  Upgrade to Chekit
                </button>
              </PermissionGate>
            )}
            {canReinstate && (
              <PermissionGate permission="riders.suspend">
                <button
                  onClick={() => reinstateMutation.mutate()}
                  disabled={reinstateMutation.isPending}
                  className="inline-flex items-center gap-1.5 rounded-full bg-[var(--brand-forest)] px-4 py-2 text-[13px] font-semibold text-white transition-all duration-200 disabled:opacity-50 hover:brightness-110 hover:shadow-[0_12px_32px_rgba(124,179,66,0.45)] active:brightness-95"
                >
                  <RotateCcw className="w-3.5 h-3.5" strokeWidth={1.5} />
                  Reinstate
                </button>
              </PermissionGate>
            )}
            {canDowngrade && (
              <PermissionGate permission="riders.upgrade">
                <button
                  onClick={() => {
                    if (confirm('Remove this rider from the Chekit fleet? They will return to merchant tier.')) {
                      downgradeMutation.mutate();
                    }
                  }}
                  disabled={downgradeMutation.isPending}
                  className="inline-flex items-center gap-1.5 rounded-full border border-[var(--border-default)] bg-white px-4 py-2 text-[13px] font-medium text-[var(--text-muted)] hover:border-[var(--border-strong)] hover:text-[var(--text-primary)] transition-all duration-200 disabled:opacity-50"
                >
                  <X className="w-3.5 h-3.5" strokeWidth={1.5} />
                  Remove from fleet
                </button>
              </PermissionGate>
            )}
            {canSuspend && (
              <PermissionGate permission="riders.suspend">
                <button
                  onClick={() => setShowSuspend(true)}
                  className="inline-flex items-center gap-1.5 rounded-full border border-[var(--state-danger-border)] bg-[var(--state-danger-bg)] px-4 py-2 text-[13px] font-medium text-[var(--state-danger)] hover:border-[var(--state-danger)] transition-all duration-200"
                >
                  <Pause className="w-3.5 h-3.5" strokeWidth={1.5} />
                  Suspend
                </button>
              </PermissionGate>
            )}
            {canDeactivate && (
              <PermissionGate permission="riders.delete">
                <button
                  onClick={() => {
                    if (confirm('Permanently deactivate this rider? This cannot be undone.')) {
                      deactivateMutation.mutate();
                    }
                  }}
                  disabled={deactivateMutation.isPending}
                  className="inline-flex items-center gap-1.5 rounded-full border border-[var(--state-danger-border)] bg-[var(--state-danger-bg)] px-4 py-2 text-[13px] font-medium text-[var(--state-danger)] hover:border-[var(--state-danger)] transition-all duration-200 disabled:opacity-50"
                >
                  <X className="w-3.5 h-3.5" strokeWidth={1.5} />
                  Deactivate
                </button>
              </PermissionGate>
            )}
          </div>
        </div>

        {actionError && (
          <div className="rounded-2xl bg-[var(--state-danger-bg)] border border-[var(--state-danger-border)] px-4 py-3 text-[13px] text-[var(--state-danger)]">
            {actionError}
          </div>
        )}

        {/* Stats row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard
            label="Deliveries"
            value={rider.totalDeliveries.toString()}
            hint={
              rider.isOnProbation && rider.probationDeliveriesRemaining != null
                ? `${rider.probationDeliveriesRemaining} left on probation`
                : undefined
            }
          />
          <StatCard
            label="Average Rating"
            value={rider.averageRating != null ? rider.averageRating.toFixed(2) : '—'}
            hint={`${rider.ratingCount} ratings`}
            icon={
              <Star
                className="w-4 h-4 text-[var(--state-warning)]"
                strokeWidth={1.5}
                fill="var(--state-warning)"
              />
            }
          />
          <StatCard
            label="Vehicle"
            value={rider.vehicleType ? rider.vehicleType : '—'}
            hint={rider.vehicleRegistration ?? undefined}
            capitalizeValue
          />
          <StatCard
            label="Licence"
            value={rider.licenceNumber ?? '—'}
          />
        </div>

        {/* Active delivery banner + force-end (for when the rider's app is stuck) */}
        {rider.activeDelivery && (
          <div className="space-y-3">
            <ActiveDeliveryBanner delivery={rider.activeDelivery} />
            <div className="flex justify-end">
              <button
                onClick={() => setShowForceEnd(true)}
                className="rounded-full border border-[var(--state-danger-border)] bg-[var(--state-danger-bg)] px-5 py-2.5 text-[13px] font-medium text-[var(--state-danger)] hover:border-[var(--state-danger)]"
              >
                Force end trip
              </button>
            </div>
          </div>
        )}

        {rider.activeDelivery && (
          <ForceEndTripModal
            open={showForceEnd}
            onClose={() => setShowForceEnd(false)}
            assignmentId={rider.activeDelivery.id}
            subject={`${fullName} · order ${rider.activeDelivery.orderId}`}
            onDone={onSuccess}
          />
        )}

        {/* Map */}
        <div className="rounded-3xl bg-white shadow-soft overflow-hidden">
          <div className="px-6 pt-5 pb-4 flex items-center justify-between">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)]">
                Location
              </p>
              {hasLiveLocation ? (
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="w-2 h-2 rounded-full bg-[var(--state-success)] animate-pulse" />
                  <span className="text-[13px] text-[var(--text-primary)] font-medium">
                    {rider.currentLatitude?.toFixed(5)}, {rider.currentLongitude?.toFixed(5)}
                  </span>
                  {locationAge && (
                    <span className="text-[12px] text-[var(--text-muted)]">· {locationAge}</span>
                  )}
                </div>
              ) : (
                <p className="text-[13px] text-[var(--text-muted)] mt-0.5">
                  Waiting for first location ping…
                </p>
              )}
            </div>
            {hasLiveLocation && (
              <span className="rounded-full bg-[var(--state-success-bg)] border border-[var(--state-success-border)] px-3 py-1 text-[11px] font-semibold text-[var(--state-success)]">
                Live · updates every 6s
              </span>
            )}
          </div>

          {!MAPS_API_KEY ? (
            <div className="mx-6 mb-6 h-[320px] rounded-2xl bg-[var(--bg-base)] flex items-center justify-center text-[13px] text-[var(--text-muted)] px-6 text-center">
              Set <code className="font-mono mx-1">VITE_GOOGLE_MAPS_API_KEY</code> in .env to render the map.
            </div>
          ) : mapsLoadError ? (
            <div className="mx-6 mb-6 h-[320px] rounded-2xl bg-[var(--state-danger-bg)] flex items-center justify-center text-[13px] text-[var(--state-danger)]">
              Failed to load Google Maps.
            </div>
          ) : !mapsLoaded ? (
            <div className="mx-6 mb-6 h-[320px] rounded-2xl bg-[var(--bg-base)] flex items-center justify-center text-[13px] text-[var(--text-muted)]">
              Loading map…
            </div>
          ) : (
            <div className="relative">
              <GoogleMap
                mapContainerStyle={MAP_CONTAINER_STYLE}
                center={mapCenter}
                zoom={hasLiveLocation ? 15 : 12}
                options={MAP_OPTIONS}
                onLoad={onMapLoad}
              >
                {/* Rider marker is placed imperatively in the mapReady effect above */}

                {/* Pickup marker */}
                {rider.activeDelivery?.pickupLatitude != null &&
                  rider.activeDelivery?.pickupLongitude != null && (
                    <Marker
                      position={{
                        lat: rider.activeDelivery.pickupLatitude,
                        lng: rider.activeDelivery.pickupLongitude,
                      }}
                      label={{ text: 'P', color: 'white', fontSize: '11px', fontWeight: '700' }}
                      title="Pickup point"
                    />
                  )}

                {/* Dropoff marker */}
                {rider.activeDelivery?.dropoffLatitude != null &&
                  rider.activeDelivery?.dropoffLongitude != null && (
                    <Marker
                      position={{
                        lat: rider.activeDelivery.dropoffLatitude,
                        lng: rider.activeDelivery.dropoffLongitude,
                      }}
                      label={{ text: 'D', color: 'white', fontSize: '11px', fontWeight: '700' }}
                      title="Dropoff point"
                    />
                  )}
              </GoogleMap>

              {/* No location overlay */}
              {!hasLiveLocation && (
                <div className="absolute inset-0 flex items-center justify-center bg-white/70 backdrop-blur-[2px]">
                  <div className="text-center px-6">
                    <MapPin className="w-8 h-8 text-[var(--text-muted)] mx-auto mb-2" strokeWidth={1} />
                    <p className="text-[14px] font-medium text-[var(--text-primary)]">No location yet</p>
                    <p className="text-[12px] text-[var(--text-muted)] mt-1">
                      Map will update once the rider shares their location.
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Availability stats */}
        {availStats && (
          <div className="rounded-3xl bg-white shadow-soft p-6">
            <div className="flex items-center justify-between mb-4">
              <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)]">
                Online Activity
              </p>
              {availStats.lastSeenAt && (
                <span className="text-[12px] text-[var(--text-muted)]">
                  Last seen {new Date(availStats.lastSeenAt).toLocaleString()}
                </span>
              )}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <StatCard label="Hrs online this week" value={availStats.hoursOnlineThisWeek.toFixed(1)} hint={`${availStats.sessionsThisWeek} sessions`} />
              <StatCard label="Hrs online (30 days)" value={availStats.hoursOnlineLast30Days.toFixed(1)} hint={`${availStats.sessionsLast30Days} sessions`} />
              <StatCard label="Avg session" value={`${availStats.avgSessionMinutes} min`} />
              <StatCard label="Total sessions" value={availStats.totalSessionsAllTime.toString()} />
            </div>
            {availStats.dailyBreakdown.length > 0 && (
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)] mb-3">
                  Last 7 days
                </p>
                <div className="flex items-end gap-1.5 h-12">
                  {availStats.dailyBreakdown.map((d) => {
                    const maxHrs = Math.max(...availStats.dailyBreakdown.map((x) => x.hoursOnline), 1);
                    const pct = Math.round((d.hoursOnline / maxHrs) * 100);
                    return (
                      <div key={d.date} className="flex flex-col items-center gap-1 flex-1" title={`${d.date}: ${d.hoursOnline}h`}>
                        <div
                          className="w-full rounded-t bg-[var(--brand-forest)] transition-all"
                          style={{ height: `${pct}%`, minHeight: pct > 0 ? '4px' : '0' }}
                        />
                        <span className="text-[9px] text-[var(--text-muted)]">{d.date.slice(5)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Contact card */}
          <div className="rounded-3xl bg-white shadow-soft p-6">
            <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)] mb-4">
              Contact
            </p>
            <DataRow icon={<Phone className="w-4 h-4 text-[var(--text-muted)]" strokeWidth={1.5} />} label="Phone" value={rider.userPhone ?? '—'} />
            <DataRow icon={<Mail className="w-4 h-4 text-[var(--text-muted)]" strokeWidth={1.5} />} label="Email" value={rider.userEmail ?? '—'} />
            <DataRow
              icon={<Calendar className="w-4 h-4 text-[var(--text-muted)]" strokeWidth={1.5} />}
              label="Joined"
              value={rider.createdAt ? new Date(rider.createdAt).toLocaleDateString() : '—'}
            />
            <DataRow
              icon={<MapPin className="w-4 h-4 text-[var(--text-muted)]" strokeWidth={1.5} />}
              label="Last location"
              value={
                rider.currentLatitude != null && rider.currentLongitude != null
                  ? `${rider.currentLatitude.toFixed(5)}, ${rider.currentLongitude.toFixed(5)}`
                  : '—'
              }
              hint={rider.lastLocationAt ? new Date(rider.lastLocationAt).toLocaleString() : undefined}
            />
          </div>

          {/* Origin / Insurance card */}
          <div className="rounded-3xl bg-white shadow-soft p-6">
            <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)] mb-4">
              Onboarding & Insurance
            </p>
            <DataRow
              icon={<Building2 className="w-4 h-4 text-[var(--text-muted)]" strokeWidth={1.5} />}
              label="Origin business"
              value={
                rider.originBusinessName ? (
                  rider.originBusinessId ? (
                    <Link
                      to={`/businesses/${rider.originBusinessId}`}
                      className="text-[var(--text-primary)] underline decoration-[var(--border-default)] hover:decoration-[var(--text-primary)]"
                    >
                      {rider.originBusinessName}
                    </Link>
                  ) : (
                    rider.originBusinessName
                  )
                ) : (
                  '—'
                )
              }
            />
            <DataRow label="Onboarding type" value={rider.onboardingType ?? '—'} capitalize />
            {rider.vouchedByBusinessId && (
              <DataRow
                icon={<Truck className="w-4 h-4 text-[var(--text-muted)]" strokeWidth={1.5} />}
                label="Vouched by"
                value={
                  <Link
                    to={`/businesses/${rider.vouchedByBusinessId}`}
                    className="text-[var(--text-primary)] underline decoration-[var(--border-default)] hover:decoration-[var(--text-primary)]"
                  >
                    View business
                  </Link>
                }
              />
            )}
            <DataRow icon={<Shield className="w-4 h-4 text-[var(--text-muted)]" strokeWidth={1.5} />} label="Insurance" value={rider.insuranceProvider ?? '—'} />
            <DataRow
              label="Insurance policy"
              value={rider.insurancePolicyNumber ?? '—'}
              hint={
                rider.insuranceExpiresAt
                  ? `expires ${new Date(rider.insuranceExpiresAt).toLocaleDateString()}`
                  : undefined
              }
            />
          </div>
        </div>

        {/* Deliveries history */}
        <div className="rounded-3xl bg-white shadow-soft overflow-hidden">
          <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-[var(--border-subtle)]">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)]">
                History
              </p>
              <p className="text-[17px] font-semibold text-[var(--text-primary)] mt-0.5">Deliveries</p>
            </div>
            <select
              value={deliveryStatusFilter}
              onChange={(e) => {
                setDeliveryStatusFilter(e.target.value);
                setDeliveriesPage(1);
              }}
              className="rounded-2xl border border-[var(--border-default)] bg-white px-4 py-2.5 text-[13px] text-[var(--text-primary)] focus:border-[var(--border-strong)] transition-colors"
            >
              <option value="">All statuses</option>
              <option value="broadcasted">Broadcasted</option>
              <option value="assigned">Assigned</option>
              <option value="accepted">Accepted</option>
              <option value="picked_up">Picked up</option>
              <option value="in_transit">In transit</option>
              <option value="delivered">Delivered</option>
              <option value="failed">Failed</option>
              <option value="reassigned">Reassigned</option>
            </select>
          </div>

          {deliveriesLoading && (
            <div className="p-10 text-center text-[14px] text-[var(--text-muted)]">Loading deliveries...</div>
          )}

          {!deliveriesLoading && (deliveriesData?.data?.length ?? 0) === 0 && (
            <div className="p-10 text-center text-[14px] text-[var(--text-muted)]">
              No deliveries on record.
            </div>
          )}

          {!deliveriesLoading && (deliveriesData?.data?.length ?? 0) > 0 && (
            <>
              <table className="w-full">
                <thead className="bg-[var(--bg-base)]">
                  <tr>
                    <th className="text-left px-6 py-3 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)]">Order</th>
                    <th className="text-left px-6 py-3 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)]">Business</th>
                    <th className="text-left px-6 py-3 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)]">Status</th>
                    <th className="text-left px-6 py-3 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)]">Distance</th>
                    <th className="text-left px-6 py-3 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)]">Rating</th>
                    <th className="text-left px-6 py-3 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)]">Assigned</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--border-subtle)]">
                  {deliveriesData!.data.map((d) => (
                    <tr key={d.id} className="hover:bg-[var(--bg-base)] transition-colors">
                      <td className="px-6 py-3">
                        <Link
                          to={`/orders/${d.orderId}`}
                          className="text-[14px] font-medium text-[var(--text-primary)] underline decoration-[var(--border-default)] hover:decoration-[var(--text-primary)]"
                        >
                          #{d.branchOrderNumber ?? d.orderId.slice(0, 8)}
                        </Link>
                        <div className="text-[12px] text-[var(--text-muted)] capitalize">
                          {d.assignmentType.replace(/_/g, ' ')}
                        </div>
                      </td>
                      <td className="px-6 py-3 text-[13px] text-[var(--text-primary)]">
                        {d.businessName || '—'}
                        {d.branchName && (
                          <div className="text-[12px] text-[var(--text-muted)]">{d.branchName}</div>
                        )}
                      </td>
                      <td className="px-6 py-3">
                        <span
                          className={cn(
                            'rounded-full px-2.5 py-1 text-[11px] font-semibold',
                            assignmentStatusChip[d.status] ?? 'bg-[var(--bg-base)] text-[var(--text-muted)]',
                          )}
                        >
                          {d.status.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="px-6 py-3 text-[13px] text-[var(--text-primary)]">
                        {d.distanceKm != null ? `${d.distanceKm.toFixed(1)} km` : '—'}
                      </td>
                      <td className="px-6 py-3">
                        {d.customerRating != null ? (
                          <span className="inline-flex items-center gap-1 text-[13px] text-[var(--text-primary)]">
                            <Star className="w-3.5 h-3.5 text-[var(--state-warning)]" strokeWidth={1.5} fill="var(--state-warning)" />
                            {d.customerRating}
                          </span>
                        ) : (
                          <span className="text-[13px] text-[var(--text-muted)]">—</span>
                        )}
                      </td>
                      <td className="px-6 py-3 text-[13px] text-[var(--text-muted)]">
                        {d.assignedAt ? new Date(d.assignedAt).toLocaleString() : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {(() => {
                const total = deliveriesData!.total;
                const totalPages = Math.max(1, Math.ceil(total / 10));
                if (totalPages <= 1) return null;
                return (
                  <div className="flex items-center justify-between px-6 py-3 border-t border-[var(--border-subtle)]">
                    <span className="text-[12px] text-[var(--text-muted)]">
                      Page {deliveriesPage} / {totalPages} · {total} total
                    </span>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setDeliveriesPage((p) => Math.max(1, p - 1))}
                        disabled={deliveriesPage === 1}
                        className="rounded-full border border-[var(--border-default)] bg-white px-3 py-1.5 text-[12px] font-medium text-[var(--text-primary)] hover:border-[var(--border-strong)] disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        Previous
                      </button>
                      <button
                        onClick={() => setDeliveriesPage((p) => Math.min(totalPages, p + 1))}
                        disabled={deliveriesPage >= totalPages}
                        className="rounded-full border border-[var(--border-default)] bg-white px-3 py-1.5 text-[12px] font-medium text-[var(--text-primary)] hover:border-[var(--border-strong)] disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                );
              })()}
            </>
          )}
        </div>
      </div>

      {showSuspend && (
        <SuspendModal
          onConfirm={(reason) => suspendMutation.mutate(reason)}
          onClose={() => setShowSuspend(false)}
          isPending={suspendMutation.isPending}
        />
      )}
    </PageGuard>
  );
};

// ── Small primitives ─────────────────────────────────────────────────────────

function ActiveDeliveryBanner({ delivery }: { delivery: DeliveryAssignment }) {
  return (
    <div className="rounded-3xl border border-[var(--border-default)] bg-[var(--bg-elevated)] p-5 flex items-start justify-between gap-4">
      <div className="flex items-start gap-3 min-w-0">
        <div className="w-10 h-10 rounded-2xl bg-[var(--brand-leaf)] text-white flex items-center justify-center shrink-0">
          <Truck className="w-5 h-5" strokeWidth={1.5} />
        </div>
        <div className="min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-accent)]">
            On delivery
          </p>
          <p className="text-[16px] font-semibold text-[var(--text-primary)] mt-0.5 flex items-center gap-2 flex-wrap">
            <Link
              to={`/orders/${delivery.orderId}`}
              className="underline decoration-[var(--border-default)] hover:decoration-[var(--text-accent)]"
            >
              #{delivery.branchOrderNumber ?? delivery.orderId.slice(0, 8)}
            </Link>
            <span
              className={cn(
                'rounded-full px-2.5 py-1 text-[11px] font-semibold',
                assignmentStatusChip[delivery.status] ?? 'bg-[var(--bg-base)] text-[var(--text-muted)]',
              )}
            >
              {delivery.status.replace(/_/g, ' ')}
            </span>
          </p>
          <p className="text-[13px] text-[var(--text-muted)] mt-1 flex items-center gap-1.5">
            <Package className="w-3.5 h-3.5" strokeWidth={1.5} />
            {delivery.businessName || '—'}
            {delivery.branchName && ` · ${delivery.branchName}`}
            {delivery.distanceKm != null && ` · ${delivery.distanceKm.toFixed(1)} km`}
          </p>
        </div>
      </div>
      <div className="text-right text-[12px] text-[var(--text-muted)] shrink-0">
        {delivery.assignedAt && (
          <div>Assigned {new Date(delivery.assignedAt).toLocaleTimeString()}</div>
        )}
        {delivery.pickupAt && (
          <div>Picked up {new Date(delivery.pickupAt).toLocaleTimeString()}</div>
        )}
      </div>
    </div>
  );
}

function StatCard({
  label,
  value,
  hint,
  icon,
  capitalizeValue,
}: {
  label: string;
  value: string;
  hint?: string;
  icon?: React.ReactNode;
  capitalizeValue?: boolean;
}) {
  return (
    <div className="rounded-3xl bg-white shadow-soft p-5">
      <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)] mb-1">
        {label}
      </p>
      <div className="flex items-center gap-2">
        {icon}
        <span className={cn('text-[20px] font-semibold text-[var(--text-primary)]', capitalizeValue && 'capitalize')}>
          {value}
        </span>
      </div>
      {hint && <p className="text-[12px] text-[var(--text-muted)] mt-0.5">{hint}</p>}
    </div>
  );
}

function DataRow({
  icon,
  label,
  value,
  hint,
  capitalize,
}: {
  icon?: React.ReactNode;
  label: string;
  value: React.ReactNode;
  hint?: string;
  capitalize?: boolean;
}) {
  return (
    <div className="flex items-start justify-between gap-3 border-b border-[var(--border-subtle)] py-3 last:border-b-0">
      <div className="flex items-center gap-2 text-[13px] text-[var(--text-muted)]">
        {icon}
        {label}
      </div>
      <div className="text-right min-w-0">
        <div className={cn('text-[14px] text-[var(--text-primary)] truncate', capitalize && 'capitalize')}>
          {value}
        </div>
        {hint && <div className="text-[12px] text-[var(--text-muted)]">{hint}</div>}
      </div>
    </div>
  );
}
